// Lógica pura del formulario 024 (Consentimiento Informado, SNS-MSP/HCU-form.024) — verificada contra la Fe de Errata AC-00115-2021
// (hojas 108-109 del visor = formulario, 110-113 = instructivo): decisión (bloque C aceptación / bloque D negativa), representante
// legal, testigo, revocatoria (bloque E) y los textos impresos del papel. Sin React ni red: la usan el wizard
// (MotorFormularioConsentimiento.tsx), la revocatoria (RevocatoriaConsentimiento024.tsx), los extractores del `Consent`
// (formulario/extractors.ts, lib/consentimientos.ts) y los tests.
import { booleanoDe, indexarRespuestas, MARCA_FIRMANTE_SERVIDOR, paresDeQuestionnaire, respuestasCrudasDe, textoDe } from '@logiacare/hce-pdf';
import type { Questionnaire, QuestionnaireResponse } from '@medplum/fhirtypes';

// --- Extensiones FHIR del `Consent` -------------------------------------------------------------------------------------------
export const EXT_FIRMA_PACIENTE = 'http://logiacare.ec/fhir/extension/firma-paciente';
/** Firma de quien firma POR el paciente (menor de edad o incapaz) — en ese caso NO se graba `firma-paciente`. */
export const EXT_FIRMA_REPRESENTANTE = 'http://logiacare.ec/fhir/extension/firma-representante';
export const EXT_FIRMA_TESTIGO = 'http://logiacare.ec/fhir/extension/firma-testigo';
/** Datos estructurados del representante legal: sub-extensiones `nombre`, `cedula`, `parentesco` (valueString). */
export const EXT_REPRESENTANTE = 'http://logiacare.ec/fhir/extension/representante-legal';
/** Datos estructurados del testigo: sub-extensiones `nombre`, `cedula` (valueString). */
export const EXT_TESTIGO = 'http://logiacare.ec/fhir/extension/testigo';

// --- Decisión ---------------------------------------------------------------------------------------------------------------
/** Textos de las dos opciones del ítem `decision` de 024.json — deben coincidir carácter por carácter (hay un test que lo verifica). */
export const DECISION_ACEPTA = 'Acepta el procedimiento (bloque C: declaración de consentimiento informado)';
export const DECISION_NIEGA = 'Se niega al procedimiento (bloque D: negativa del consentimiento informado)';

export type Decision = 'acepta' | 'niega';

/**
 * Decisión del paciente. Los 024 nuevos traen el ítem `decision`; los ya firmados (y los otros 6 consentimientos del catálogo)
 * traen el booleano `declaracion-general`, que se sigue leyendo. `undefined` = no contestó (se trata como no aceptado).
 */
export function decisionDe(response: QuestionnaireResponse): Decision | undefined {
  const r = indexarRespuestas(response);
  const d = textoDe(r, 'decision');
  if (d === DECISION_ACEPTA) return 'acepta';
  if (d === DECISION_NIEGA) return 'niega';
  if (r.has('declaracion-general')) return booleanoDe(r, 'declaracion-general') ? 'acepta' : 'niega';
  return undefined;
}

// --- Fecha y hora (Ecuador continental: UTC-5 todo el año, sin horario de verano) -------------------------------------------------
const OFFSET_EC_MS = 5 * 60 * 60 * 1000;
/** Margen para el desfase entre el reloj del equipo y el del servidor al validar que una fecha/hora no sea futura. */
export const TOLERANCIA_FUTURO_MS = 5 * 60 * 1000;

export function fechaYHoraEcuador(ahora: Date): { fecha: string; hora: string } {
  const iso = new Date(ahora.getTime() - OFFSET_EC_MS).toISOString();
  return { fecha: iso.slice(0, 10), hora: iso.slice(11, 16) };
}

function respuestaCruda(response: QuestionnaireResponse, linkId: string): { valueDate?: string; valueTime?: string } | undefined {
  return respuestasCrudasDe(response, linkId)[0];
}

function sufijoDecision(decision: Decision | undefined): 'informacion' | 'negativa' {
  return decision === 'niega' ? 'negativa' : 'informacion';
}

/** Fecha (`YYYY-MM-DD`) y hora (`HH:mm`) ya capturadas para la decisión vigente (`fecha-informacion`/`hora-informacion` o `fecha-negativa`/`hora-negativa`). */
export function fechaHoraCapturada(response: QuestionnaireResponse): { fecha?: string; hora?: string } {
  const suf = sufijoDecision(decisionDe(response));
  return { fecha: respuestaCruda(response, `fecha-${suf}`)?.valueDate, hora: respuestaCruda(response, `hora-${suf}`)?.valueTime?.slice(0, 5) };
}

/** Instante ISO de la decisión: la fecha/hora capturadas (hora local de Ecuador) o, si no hay, `ahora`. */
export function instanteDeLaDecision(response: QuestionnaireResponse, ahora: Date): string {
  const { fecha, hora } = fechaHoraCapturada(response);
  if (!fecha) return ahora.toISOString();
  const instante = new Date(`${fecha}T${hora ?? '00:00'}:00-05:00`);
  return Number.isNaN(instante.getTime()) ? ahora.toISOString() : instante.toISOString();
}

/**
 * Completa con la fecha y hora actuales lo que el profesional dejó en blanco (el instructivo pide registrar la fecha y la hora en que se
 * da la información / se decide la negativa). Lo que sí escribió no se toca. Solo para el 024 (los ítems `fecha-*`/`hora-*` son suyos).
 */
export function completarFechaHoraDecision(response: QuestionnaireResponse, ahora: Date): QuestionnaireResponse {
  const decision = decisionDe(response);
  if (!decision) return response;
  const suf = sufijoDecision(decision);
  const { fecha: hoy, hora: horaAhora } = fechaYHoraEcuador(ahora);
  const item = [...(response.item ?? [])];
  const capturada = fechaHoraCapturada(response);
  if (!capturada.fecha) item.push({ linkId: `fecha-${suf}`, answer: [{ valueDate: hoy }] });
  if (!capturada.hora) item.push({ linkId: `hora-${suf}`, answer: [{ valueTime: `${horaAhora}:00` }] });
  return { ...response, item };
}

/** `2026-09-23` → `23/09/2026` (el instructivo pide día, mes, año). */
export function fechaParaImpresion(fechaIso: string): string {
  const [a, m, d] = fechaIso.split('-');
  return a && m && d ? `${d}/${m}/${a}` : fechaIso;
}

/** `14:30` → `14h30` (el instructivo da como ejemplo `12h:30`; se conserva ese formato). */
export function horaParaImpresion(hora: string): string {
  return hora.replace(':', 'h:');
}

// --- Representante legal / testigo ------------------------------------------------------------------------------------------------
export interface PersonaConsentimiento {
  nombre?: string;
  cedula?: string;
  parentesco?: string;
}

/**
 * Servicio del encabezado del PDF del 024: el que el profesional escribe en el ítem `servicio` del bloque B del papel. Si lo dejó
 * vacío se usa el `tipo-atencion` marcado: "Hospitalización" o, si es "Ambulatorio", "Consulta externa". `undefined` = el formulario
 * no dice nada: el encabezado NO imprime servicio (`sinServicio`), en vez de afirmar "Consulta externa" sin base.
 */
export function servicioAtencion024(response: QuestionnaireResponse): string | undefined {
  const r = indexarRespuestas(response);
  const tipo = textoDe(r, 'tipo-atencion');
  return textoDe(r, 'servicio') ?? (tipo === 'Hospitalización' ? 'Hospitalización' : tipo === 'Ambulatorio' ? 'Consulta externa' : undefined);
}

/** ¿Firma un representante legal en lugar del paciente (menor de edad o incapaz)? Solo el 024 nuevo trae la casilla. */
export function firmaPorRepresentante(response: QuestionnaireResponse): boolean {
  return booleanoDe(indexarRespuestas(response), 'firma-representante');
}

export function representanteDe(response: QuestionnaireResponse): PersonaConsentimiento {
  const r = indexarRespuestas(response);
  return { nombre: textoDe(r, 'representante-nombre'), cedula: textoDe(r, 'representante-cedula'), parentesco: textoDe(r, 'representante-parentesco') };
}

/** Errores de captura del 024 (claves i18n de `consentimiento.error.*`). Vacío = se puede continuar a la firma. */
export function errores024(response: QuestionnaireResponse, ahora: Date = new Date()): string[] {
  const errores: string[] = [];
  if (!decisionDe(response)) errores.push('decisionFalta');
  // La fecha/hora quedan en el Consent y en el PDF firmado (que no se puede corregir): ni futuras, ni una fecha suelta con la hora de hoy.
  const { fecha, hora } = fechaHoraCapturada(response);
  if (fecha) {
    if (!hora && fecha !== fechaYHoraEcuador(ahora).fecha) errores.push('faltaHora');
    if (instanteDeLaDecision(response, ahora) > new Date(ahora.getTime() + TOLERANCIA_FUTURO_MS).toISOString()) errores.push('fechaFutura');
  } else if (hora) {
    errores.push('faltaFecha');
  }
  if (firmaPorRepresentante(response)) {
    const rep = representanteDe(response);
    if (!rep.nombre || !rep.cedula || !rep.parentesco) errores.push('representanteIncompleto');
  }
  return errores;
}

/** Extensión FHIR con sub-extensiones `valueString`; omite los campos vacíos. `undefined` si no queda ninguno. */
export function extensionPersona(url: string, persona: PersonaConsentimiento): { url: string; extension: { url: string; valueString: string }[] } | undefined {
  const extension = (['nombre', 'cedula', 'parentesco'] as const).flatMap((campo) => (persona[campo] ? [{ url: campo, valueString: persona[campo] }] : []));
  return extension.length > 0 ? { url, extension } : undefined;
}

/** Texto legible de una persona para `Consent.provision.actor.reference.display`: `Nombre (C.I. 123) — Parentesco: madre`. */
export function textoPersona(persona: PersonaConsentimiento): string {
  const base = [persona.nombre, persona.cedula ? `(C.I. ${persona.cedula})` : undefined].filter(Boolean).join(' ');
  return persona.parentesco ? `${base} — Parentesco: ${persona.parentesco}` : base;
}

// --- Textos impresos del papel (Fe de Errata AC-00115-2021, hoja 109 del visor) ---------------------------------------------------
export const TEXTO_DECLARACION_C =
  'He facilitado la información completa que conozco, y me ha sido solicitada, sobre los antecedentes personales, familiares y de mi estado de salud. Soy consciente que de omitir estos datos puede afectarse los resultados del tratamiento. Estoy de acuerdo con el procedimiento que se me ha propuesto; he sido informado de las ventajas e inconvenientes del mismo; se me ha explicado de forma clara en qué consiste, los beneficios y posibles riesgos del procedimiento. He escuchado, leído y comprendido la información recibida y se me ha dado la oportunidad de preguntar sobre el procedimiento. He tomado consciente y libremente de decisión de autorizar el procedimiento adicional, si es considerado necesario según el juicio del profesional de la salud, para mi beneficio. También conozco que puedo retirar mi consentimiento cuando lo estime oportuno.';

export const TEXTO_NEGATIVA_D =
  'Una vez que he entendido claramente el procedimiento propuesto, así como las consecuencias posibles si no se realiza la intervención, no autorizo y me niego a que se me realice el procedimiento propuesto y desvinculo de responsabilidades futuras de cualquier índole al establecimiento de salud y al profesional sanitario que me atiende, por no realizar la intervención sugerida.';

/** Bloque E: el papel deja dos espacios en blanco ("en fecha" y "en esta fecha:") — se llenan con la fecha del consentimiento y la de la revocatoria. */
export function textoRevocatoriaE(fechaConsentimiento: string, fechaRevocatoria: string): string {
  return `De forma libre y voluntaria, revoco el consentimiento realizado en fecha ${fechaConsentimiento} y manifiesto expresamente mi deseo de no continuar con el procedimiento médico que doy por finalizado en esta fecha: ${fechaRevocatoria}. Libero de responsabilidades futuras de cualquier índole al establecimiento de salud y al profesional sanitario que me atiende.`;
}

const ROTULO_PACIENTE = 'Nombre completo del paciente';
const ROTULO_CEDULA = 'Cédula de ciudadanía';
const ROTULO_PROFESIONAL = 'Nombre de profesional que realiza el procedimiento — Firma, sello y código del profesional de la salud que realizará el procedimiento';
const ROTULO_SIN_CAPACIDAD = 'Si el paciente no está en capacidad para firmar el consentimiento informado';

export interface DatosPacientePdf {
  nombre: string;
  cedula?: string;
}

export type MetodoFirma = 'tableta' | 'escaneo';

export interface FirmanteConsentimiento {
  /** Quién firmó el documento: el paciente (o su representante, según `firmaPorRepresentante`) o, si el paciente se negó a firmar la negativa, un testigo. */
  rol: 'paciente' | 'testigo';
  metodo?: MetodoFirma;
  testigo?: { nombre: string; cedula: string };
}

function textoMetodoFirma(metodo: MetodoFirma | undefined): string {
  return metodo === 'escaneo' ? 'Formulario firmado en papel, escaneado y adjunto al expediente' : 'Firma manuscrita digitalizada (tableta), registrada en el expediente';
}

/** Ítems de 024.json que se imprimen aparte con el rótulo del papel (no por el listado genérico pregunta/respuesta). */
const ITEMS_APARTE = new Set(['diagnostico', 'decision', 'fecha-informacion', 'hora-informacion', 'fecha-negativa', 'hora-negativa', 'firma-representante', 'representante-legal']);

interface EntradaPares024 {
  questionnaire: Questionnaire;
  response: QuestionnaireResponse;
  paciente: DatosPacientePdf;
  firmante: FirmanteConsentimiento;
}

function bloqueFirmas(paciente: DatosPacientePdf, decision: Decision, response: QuestionnaireResponse, firmante: FirmanteConsentimiento): { pregunta: string; respuesta: string }[] {
  const porRepresentante = firmaPorRepresentante(response);
  const pares: { pregunta: string; respuesta: string }[] = [
    { pregunta: ROTULO_PACIENTE, respuesta: paciente.nombre || '—' },
    { pregunta: ROTULO_CEDULA, respuesta: paciente.cedula || '—' },
  ];
  if (firmante.rol === 'testigo') {
    pares.push({ pregunta: 'Firma del paciente o huella, según el caso', respuesta: 'El paciente se niega a firmar (firma el testigo)' });
  } else if (porRepresentante) {
    pares.push({ pregunta: 'Firma del paciente o huella, según el caso', respuesta: 'No firma el paciente: firma su representante legal' });
  } else {
    pares.push({ pregunta: 'Firma del paciente o huella, según el caso', respuesta: textoMetodoFirma(firmante.metodo) });
  }
  pares.push({ pregunta: ROTULO_PROFESIONAL, respuesta: MARCA_FIRMANTE_SERVIDOR });
  if (porRepresentante) {
    const rep = representanteDe(response);
    pares.push(
      { pregunta: ROTULO_SIN_CAPACIDAD, respuesta: 'Firma el representante legal' },
      { pregunta: 'Nombre del representante legal', respuesta: rep.nombre ?? '—' },
      { pregunta: ROTULO_CEDULA, respuesta: rep.cedula ?? '—' },
      { pregunta: 'Parentesco', respuesta: rep.parentesco ?? '—' },
      { pregunta: 'Firma del representante legal', respuesta: firmante.rol === 'testigo' ? 'Pendiente (firma el testigo)' : textoMetodoFirma(firmante.metodo) },
    );
  }
  if (decision === 'niega' && firmante.rol === 'testigo' && firmante.testigo) {
    pares.push(
      { pregunta: 'Si el paciente no acepta el procedimiento sugerido por el profesional y se niega a firmar este acápite', respuesta: 'Firma un testigo' },
      { pregunta: 'Nombre completo del testigo', respuesta: firmante.testigo.nombre },
      { pregunta: `Testigo — ${ROTULO_CEDULA}`, respuesta: firmante.testigo.cedula },
      { pregunta: 'Firma del testigo', respuesta: 'Firma manuscrita digitalizada (tableta), registrada en el expediente' },
    );
  }
  return pares;
}

/**
 * Pares del PDF firmado del 024 (modo `msp`): el bloque B en el orden del formulario (con el CIE-10 en su propio renglón, como el papel),
 * y después el bloque C (aceptación) o D (negativa) con el TEXTO del papel, fecha/hora, datos del paciente, del profesional que
 * realiza el procedimiento, del representante legal y del testigo. El bloque A lo imprime el encabezado MSP.
 */
export function paresConsentimiento024({ questionnaire, response, paciente, firmante }: EntradaPares024): { pregunta: string; respuesta: string }[] {
  const pares: { pregunta: string; respuesta: string }[] = [];
  for (const item of questionnaire.item ?? []) {
    if (item.linkId === 'diagnostico') {
      const respuestas = respuestasCrudasDe(response, 'diagnostico');
      const nombres = respuestas.map((a) => a.valueCoding?.display ?? a.valueString ?? a.valueCoding?.code).filter((v): v is string => !!v);
      const codigos = respuestas.map((a) => a.valueCoding?.code).filter((v): v is string => !!v);
      if (nombres.length > 0) pares.push({ pregunta: 'Diagnóstico', respuesta: nombres.join(', ') });
      if (codigos.length > 0) pares.push({ pregunta: 'CIE-10', respuesta: codigos.join(', ') });
      continue;
    }
    if (ITEMS_APARTE.has(item.linkId)) continue;
    pares.push(...paresDeQuestionnaire({ ...questionnaire, item: [item] }, response));
  }

  const decision = decisionDe(response);
  if (decision) {
    const { fecha, hora } = fechaHoraCapturada(response);
    const cuando = [fecha ? `Fecha: ${fechaParaImpresion(fecha)}` : undefined, hora ? `Hora: ${horaParaImpresion(hora)}` : undefined].filter(Boolean).join(' · ');
    if (decision === 'acepta') {
      pares.push({ pregunta: 'C. DECLARACIÓN DE CONSENTIMIENTO INFORMADO', respuesta: cuando || '—' }, { pregunta: 'Declaración', respuesta: TEXTO_DECLARACION_C });
    } else {
      pares.push({ pregunta: 'D. NEGATIVA DEL CONSENTIMIENTO INFORMADO', respuesta: cuando || '—' }, { pregunta: 'Negativa', respuesta: TEXTO_NEGATIVA_D });
    }
    pares.push(...bloqueFirmas(paciente, decision, response, firmante));
  }
  return pares;
}

export interface EntradaParesRevocatoria {
  paciente: DatosPacientePdf;
  /** Fecha (ISO) de firma del consentimiento que se revoca. */
  fechaConsentimientoISO: string;
  /** `YYYY-MM-DD` y `HH:mm` de la revocatoria. */
  fecha: string;
  hora: string;
  representante?: PersonaConsentimiento;
  metodoFirma?: MetodoFirma;
}

/** Pares del PDF firmado de la revocatoria (bloque E del papel), modo `msp`. */
export function paresRevocatoria024({ paciente, fechaConsentimientoISO, fecha, hora, representante, metodoFirma }: EntradaParesRevocatoria): { pregunta: string; respuesta: string }[] {
  const { fecha: fechaConsentimiento } = fechaYHoraEcuador(new Date(fechaConsentimientoISO));
  const pares: { pregunta: string; respuesta: string }[] = [
    { pregunta: 'E. REVOCATORIA DEL CONSENTIMIENTO INFORMADO', respuesta: `Fecha: ${fechaParaImpresion(fecha)} · Hora: ${horaParaImpresion(hora)}` },
    { pregunta: 'Revocatoria', respuesta: textoRevocatoriaE(fechaParaImpresion(fechaConsentimiento), fechaParaImpresion(fecha)) },
    { pregunta: ROTULO_PACIENTE, respuesta: paciente.nombre || '—' },
    { pregunta: ROTULO_CEDULA, respuesta: paciente.cedula || '—' },
    {
      pregunta: 'Firma del paciente o huella, según el caso',
      respuesta: representante ? 'No firma el paciente: firma su representante legal' : textoMetodoFirma(metodoFirma),
    },
    { pregunta: ROTULO_PROFESIONAL, respuesta: MARCA_FIRMANTE_SERVIDOR },
  ];
  if (representante) {
    pares.push(
      { pregunta: ROTULO_SIN_CAPACIDAD, respuesta: 'Firma el representante legal' },
      { pregunta: 'Nombre del representante legal', respuesta: representante.nombre ?? '—' },
      { pregunta: ROTULO_CEDULA, respuesta: representante.cedula ?? '—' },
      { pregunta: 'Parentesco', respuesta: representante.parentesco ?? '—' },
      { pregunta: 'Firma del representante legal', respuesta: textoMetodoFirma(metodoFirma) },
    );
  }
  return pares;
}
